import { useState, useEffect, useCallback } from 'react';

var API = 'https://api.algrok.com';

function fmtINR(n) {
  if (n == null || isNaN(n)) return '--';
  var sign = n < 0 ? '\u2212' : '+';
  return sign + '\u20b9' + Math.abs(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

function StatBox(props) {
  return (
    <div style={{ flex: 1, minWidth: 92, background: '#0a0f1c', border: '1px solid #1f2937', borderRadius: 10, padding: '8px 12px', textAlign: 'center' }}>
      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.8, color: '#556677' }}>{props.label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: props.color || '#e5e7eb', marginTop: 2 }}>{props.value}</div>
      {props.sub ? <div style={{ fontSize: 9, color: '#6b7a8d', marginTop: 1 }}>{props.sub}</div> : null}
    </div>
  );
}

function Field(props) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 132, flex: 1 }}>
      <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.6, color: '#556677' }}>{props.label}</span>
      <input value={props.value == null ? '' : props.value} onChange={props.onChange}
        style={{ background: '#0d1524', border: '1px solid #26334a', borderRadius: 6, color: '#e5e7eb', fontSize: 12, padding: '6px 9px', fontFamily: 'inherit' }} />
    </label>
  );
}

var cardBase = { background: '#0a0f1c', border: '1px solid #1f2937', borderRadius: 12, padding: '12px 14px' };

var DirectionalTV1 = function () {
  var [st, setSt] = useState({});
  var [cfgOpen, setCfgOpen] = useState(false);
  var [draft, setDraft] = useState({});
  var [busy, setBusy] = useState(false);
  var [err, setErr] = useState('');

  var load = useCallback(function () {
    fetch(API + '/directional-tv-1/state').then(function (r) { return r.json(); })
      .then(function (d) { setSt(d || {}); setErr((d && d.error) || ''); })
      .catch(function () { setErr('unreachable'); });
  }, []);

  useEffect(function () {
    load();
    // 1s — the header shows spot, future and synthetic live, and a reversal can
    // land between polls, so a 5s refresh made the page lag the position.
    var id = setInterval(load, 1000);
    return function () { clearInterval(id); };
  }, [load]);

  function post(path, body) {
    setBusy(true);
    fetch(API + path, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
    }).then(function (r) { return r.json(); })
      .then(function () { setBusy(false); load(); })
      .catch(function () { setBusy(false); });
  }

  var cfg = st.config || {};
  var legs = st.legs || [];
  var openLegs = legs.filter(function (l) { return l.status === 'open'; });
  var sig = st.signal || {};
  var enabled = !!st.enabled;
  var total = st.total_pnl || 0;
  var halt = st.halt_reason || '';

  function openCfg() {
    setDraft({
      lots: cfg.lots != null ? cfg.lots : 1,
      product: cfg.product || 'MIS',
      entry_time: cfg.entry_time || '09:17',
      exit_time: cfg.exit_time || '15:25',
      mode: cfg.mode || 'paper',
      strike_ref: cfg.strike_ref || 'spot',
      no_trade_after: cfg.no_trade_after || '',
      max_price_age_sec: cfg.max_price_age_sec != null ? cfg.max_price_age_sec : 10,
      structure: cfg.structure || 'debit',
      carry_overnight: !!cfg.carry_overnight,
      webhook_token: cfg.webhook_token || '',
      wing_offset_normal: cfg.wing_offset_normal != null ? cfg.wing_offset_normal : 2,
      wing_offset_expiry: cfg.wing_offset_expiry != null ? cfg.wing_offset_expiry : 1,
      mtm_sl_per_lot: cfg.mtm_sl_per_lot || 0,
      target_per_lot: cfg.target_per_lot || 0,
      protect_per_lot: cfg.protect_per_lot || 0,
      protect_level_per_lot: cfg.protect_level_per_lot || 0,
      trail_step_per_lot: cfg.trail_step_per_lot || 0,
    });
    // Pull the FULL config — /state carries only a masked token, so the real
    // value has to come from here for the field to be editable.
    fetch(API + '/directional-tv-1/config')
      .then(function (r) { return r.json(); })
      .then(function (c) { if (c && !c.error) setDraft(function (p) { return Object.assign({}, p, c); }); })
      .catch(function () {});
    setCfgOpen(true);
  }
  function D(k) {
    return function (e) {
      var v = e.target.value;
      setDraft(function (p) { var n = Object.assign({}, p); n[k] = v; return n; });
    };
  }
  // Fields that are text, not numbers. Anything else is coerced BY TYPE below
  // rather than by a hardcoded list — a list has to be remembered every time a
  // setting is added, and forgetting once silently zeroes it.
  var TEXT_KEYS = {
    product: 1, entry_time: 1, exit_time: 1, mode: 1, structure: 1,
    webhook_token: 1, strike_ref: 1, no_trade_after: 1, index: 1,
  };

  function saveCfg() {
    var out = {};
    Object.keys(draft).forEach(function (k) {
      var v = draft[k];

      // NEVER post `enabled` from settings. Enable/disable is its own button,
      // and the full-config fetch (added so the token field could be edited)
      // merges every key into the draft — including this one. It went through
      // parseFloat, became NaN, fell back to 0, and every settings save
      // silently DISABLED the strategy. It persisted too, so restarts stayed
      // off.
      if (k === 'enabled') return;

      // Booleans by type. qty_guard_enabled and qty_guard_halt_on_breach also
      // arrive from that fetch, and zeroing them would quietly switch off the
      // quantity guard — a safety layer, not a preference.
      if (typeof v === 'boolean') { out[k] = v; return; }
      if (k === 'carry_overnight') { out[k] = (v === true || v === 'true' || v === 1 || v === '1'); return; }

      if (TEXT_KEYS[k]) { out[k] = v; return; }

      // Numbers, but a failed parse KEEPS the original rather than falling back
      // to 0. That fallback is what turned 'NIFTY' into 0 and posted it.
      var n = parseFloat(v);
      out[k] = isNaN(n) ? v : n;
    });
    post('/directional-tv-1/config', out);
    setCfgOpen(false);
  }

  var selStyle = {
    background: '#0d1524', border: '1px solid #26334a', borderRadius: 6,
    padding: '7px 9px', color: '#e5e7eb', fontSize: 11, fontFamily: 'inherit',
  };

  var dirColor = st.direction === 'LONG' ? '#22c55e' : st.direction === 'SHORT' ? '#ef4444' : '#6b7a8d';

  return (
    <div style={{ padding: '18px 22px', color: '#e5e7eb', fontFamily: 'inherit', maxWidth: 1400 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
        <span style={{ fontSize: 22, fontWeight: 800 }}>🎯 Directional TV-1</span>
        {/* Both chips are DERIVED. The clone inherited "REVERSED · FADES
            SIGNAL" (that is OI Ride D — this one FOLLOWS the signal) and a
            hardcoded "REAL MONEY" that stayed red while the strategy was in
            paper. A label that contradicts the actual mode is worse than none. */}
        <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1, padding: '3px 8px', borderRadius: 6, background: 'rgba(167,139,250,0.14)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.4)' }}>
          {(st.structure || 'debit').toUpperCase()} · FOLLOWS SIGNAL
        </span>
        {st.mode === 'live' ? (
          <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1, padding: '3px 8px', borderRadius: 6, background: 'rgba(239,68,68,0.14)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.4)' }}>
            REAL MONEY
          </span>
        ) : (
          <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1, padding: '3px 8px', borderRadius: 6, background: 'rgba(56,189,248,0.14)', color: '#38bdf8', border: '1px solid rgba(56,189,248,0.4)' }}>
            PAPER · NO ORDERS
          </span>
        )}
        {st.carrying_overnight ? (
          <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1, padding: '3px 8px', borderRadius: 6, background: 'rgba(251,191,36,0.12)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.5)' }}>
            CARRY OVERNIGHT
          </span>
        ) : null}
        {st.past_no_trade_cutoff ? (
          <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1, padding: '3px 8px', borderRadius: 6, background: 'rgba(148,163,184,0.14)', color: '#94a3b8', border: '1px solid rgba(148,163,184,0.45)' }}>
            NO NEW TRADES · after {st.no_trade_after}
          </span>
        ) : null}

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, color: enabled ? '#22c55e' : '#6b7a8d' }}>
            {enabled ? '\u25cf ENABLED' : '\u25cb DISABLED'}
          </span>
          {/* Simple toggle, no confirmation — the bought wing caps the risk. */}
          <button onClick={function () { post(enabled ? '/directional-tv-1/disable' : '/directional-tv-1/enable'); }}
            disabled={busy}
            style={{
              fontSize: 11, fontWeight: 800, cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
              borderRadius: 8, padding: '7px 18px',
              background: enabled ? 'rgba(239,68,68,0.12)' : 'rgba(34,197,94,0.12)',
              color: enabled ? '#ef4444' : '#22c55e',
              border: '1px solid ' + (enabled ? 'rgba(239,68,68,0.5)' : 'rgba(34,197,94,0.5)'),
            }}>
            {enabled ? 'Disable' : 'Enable'}
          </button>
          <button onClick={openCfg}
            style={{ fontSize: 11, fontWeight: 800, cursor: 'pointer', background: 'transparent', color: '#8b98a9', border: '1px solid #1f2937', borderRadius: 8, padding: '7px 14px', fontFamily: 'inherit' }}>⚙</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        {[['SPOT', st.px_spot, 'spot'], ['FUTURE', st.px_future, 'future'],
          ['SYNTHETIC', st.px_synthetic, 'synthetic']].map(function (p) {
          var isRef = (st.strike_ref || 'spot') === p[2];
          var v = p[1];
          var age = p[2] === 'spot' ? st.age_spot : p[2] === 'future' ? st.age_future : st.age_synthetic;
          return (
            <div key={p[2]} style={{
              flex: 1, minWidth: 130, background: '#0a0f1c',
              border: '1px solid ' + (isRef ? 'rgba(14,165,233,0.5)' : '#1f2937'),
              borderRadius: 10, padding: '8px 12px',
            }}>
              <div style={{ fontSize: 8, fontWeight: 800, letterSpacing: 1,
                color: isRef ? '#0ea5e9' : '#556677' }}>
                {p[0]}{isRef ? ' · ANCHOR' : ''}
              </div>
              <div style={{ fontSize: 17, fontWeight: 800, color: v ? '#e5e7eb' : '#556677' }}>
                {v ? Number(v).toFixed(2) : '--'}
              </div>
              <div style={{ fontSize: 9, display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                <span style={{ color: (p[2] !== 'spot' && v && st.px_spot)
                  ? ((v - st.px_spot) >= 0 ? '#22c55e' : '#ef4444') : '#556677' }}>
                  {p[2] !== 'spot' && v && st.px_spot
                    ? ((v - st.px_spot) >= 0 ? '+' : '') + (v - st.px_spot).toFixed(2) + ' vs spot'
                    : '\u00a0'}
                </span>
                {/* Age matters because a stale price never blanks — it just
                    keeps showing the last good value. During live hours a price
                    past the limit is REFUSED as a trade anchor, so it is shown
                    in red rather than left looking current. */}
                <span style={{ color: age == null ? '#556677'
                  : (st.enforcing_freshness && age > (st.max_price_age_sec || 10)) ? '#ef4444'
                  : age > 5 ? '#fbbf24' : '#556677' }}>
                  {age == null ? '' : age < 1 ? 'now' : Math.round(age) + 's ago'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: 10, color: '#6b7a8d', marginBottom: 12 }}>
        NIFTY · {cfg.lots || 1}L · {cfg.product || 'MIS'} · {(st.structure || 'debit').toUpperCase()} ·
        {' '}entry {cfg.entry_time || '09:15'} · exit {st.carrying_overnight ? 'CARRY OVERNIGHT' : (cfg.exit_time || '15:39')} ·
        {' '}wing {cfg.wing_offset_normal || 2} strikes · {st.mode === 'live' ? 'LIVE' : 'PAPER'}
      </div>

      {halt ? (
        <div style={{ ...cardBase, borderColor: 'rgba(239,68,68,0.5)', color: '#f87171', fontSize: 11, marginBottom: 12 }}>
          ☠ SAFETY HALT — {halt}. Check Kite, then press Enable to clear it.
        </div>
      ) : null}
      {st.day_stopped ? (
        <div style={{ ...cardBase, borderColor: 'rgba(251,191,36,0.45)', color: '#fbbf24', fontSize: 11, marginBottom: 12 }}>
          Day stopped — {st.day_stopped}. No further entries today.
        </div>
      ) : null}
      {err ? (
        <div style={{ ...cardBase, borderColor: 'rgba(251,191,36,0.45)', color: '#fbbf24', fontSize: 11, marginBottom: 12 }}>
          Directional TV-1: {err}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <StatBox label="TOTAL P&L" value={fmtINR(total)} color={total >= 0 ? '#22c55e' : '#ef4444'} />
        <StatBox label="REALIZED" value={fmtINR(st.realized_pnl || 0)} />
        <StatBox label="UNREALIZED" value={fmtINR(st.unrealized_pnl || 0)} />
        <StatBox label="DIRECTION" value={st.direction || '--'} color={dirColor} sub={st.active ? 'in position' : 'flat'} />
        <StatBox label="SIGNAL" value={sig.pct != null ? sig.pct + '%' : '--'} sub={sig.verdict || ''} />
        <StatBox label="NET CREDIT" value={st.entry_premium != null ? String(st.entry_premium) : '--'} />
        <StatBox label="FLIPS" value={st.flips != null ? String(st.flips) : '0'} />
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1.2, minWidth: 340, ...cardBase }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, color: '#556677', marginBottom: 8 }}>
            POSITION {st.atm ? '· ATM ' + st.atm : ''} {st.spot ? '· spot ' + st.spot : ''}
          </div>
          {openLegs.length ? openLegs.map(function (l, i) {
            var isSell = l.side === 'SELL';
            return (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '6px 0', borderBottom: i === 0 ? '1px solid #131c2b' : 'none', fontSize: 12 }}>
                <span style={{ color: isSell ? '#ef4444' : '#22c55e', fontWeight: 800, minWidth: 44 }}>{l.side}</span>
                <span style={{ flex: 1 }}>{l.strike}{l.type} <span style={{ color: '#6b7a8d', fontSize: 10 }}>{l.role}</span></span>
                <span style={{ color: '#8b98a9', minWidth: 108, textAlign: 'right' }}>{l.entry_price} → {l.ltp}</span>
                <span style={{ minWidth: 84, textAlign: 'right', fontWeight: 800, color: (l.pnl || 0) >= 0 ? '#22c55e' : '#ef4444' }}>{fmtINR(l.pnl || 0)}</span>
              </div>
            );
          }) : (
            <div style={{ fontSize: 11, color: '#6b7a8d', padding: '10px 0' }}>Flat — no open legs.</div>
          )}
          {st.active ? (
            <button onClick={function () { post('/directional-tv-1/exit'); }} disabled={busy}
              style={{ marginTop: 10, fontSize: 11, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', borderRadius: 8, padding: '7px 16px', background: 'rgba(239,68,68,0.12)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.5)' }}>
              Exit now
            </button>
          ) : null}
        </div>

        <div style={{ flex: 1, minWidth: 280, ...cardBase }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, color: '#556677', marginBottom: 8 }}>TRADINGVIEW SIGNAL</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0' }}>
            <span style={{ color: '#8b98a9' }}>Position</span>
            <span style={{ fontWeight: 800, color: dirColor }}>{st.direction || 'FLAT'}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0' }}>
            <span style={{ color: '#8b98a9' }}>Last signal</span><span>{st.last_signal_at ? String(st.last_signal_at).slice(11, 19) : '--'}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0' }}>
            <span style={{ color: '#8b98a9' }}>Webhook token</span>
            <span style={{ color: st.webhook_token_set ? '#22c55e' : '#ef4444', fontWeight: 800 }}>
              {st.webhook_token_set ? '\u2022\u2022\u2022' + st.webhook_token_tail : 'NOT SET'}
            </span>
          </div>
          <div style={{ fontSize: 9, color: '#556677', marginTop: 8, lineHeight: 1.5 }}>
            {st.webhook_token_set
              ? 'A same-side signal is ignored — it never stacks. An opposite signal exits fully, then enters the other way.'
              : 'No token configured, so every webhook is rejected. Generate one in settings and paste it into the TradingView alert.'}
          </div>
        </div>
      </div>

      <div style={{ ...cardBase, padding: '4px 0' }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, color: '#556677', padding: '8px 14px 4px' }}>EVENTS</div>
        {(st.events || []).length ? st.events.slice().reverse().slice(0, 40).map(function (e, i) {
          var lv = e.level || e.type;
              var c = lv === 'error' ? '#f87171' : lv === 'warning' ? '#fbbf24' : lv === 'success' ? '#22c55e' : '#8b98a9';
          return (
            <div key={i} style={{ display: 'flex', gap: 10, padding: '3px 14px', fontSize: 11, fontFamily: 'ui-monospace, Menlo, monospace' }}>
              <span style={{ color: '#556677', flexShrink: 0 }}>{e.time || e.ts}</span>
              <span style={{ color: c, flex: 1, wordBreak: 'break-word' }}>{e.text || e.msg}</span>
            </div>
          );
        }) : <div style={{ fontSize: 11, color: '#6b7a8d', padding: '10px 14px' }}>No events yet.</div>}
      </div>

      {cfgOpen ? (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(3,7,18,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}
          onClick={function () { setCfgOpen(false); }}>
          <div onClick={function (e) { e.stopPropagation(); }}
            style={{ ...cardBase, width: 640, maxHeight: '82vh', overflowY: 'auto' }}>
            <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 12 }}>Directional TV-1 — settings</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
              <Field label="Lots" value={draft.lots} onChange={D('lots')} />
              <div>
                <div style={{ fontSize: 9, color: '#6b7a8d', marginBottom: 3 }}>Product</div>
                <select value={draft.product || 'MIS'} onChange={D('product')} style={selStyle}>
                  <option value="MIS">MIS (intraday)</option>
                  <option value="NRML">NRML</option>
                </select>
              </div>
              <Field label="Entry time" value={draft.entry_time} onChange={D('entry_time')} />
              <Field label="Exit time" value={draft.exit_time} onChange={D('exit_time')} />
              <Field label="No trade after (blank = off)" value={draft.no_trade_after} onChange={D('no_trade_after')} />
              <Field label="Max price age (s)" value={draft.max_price_age_sec} onChange={D('max_price_age_sec')} />
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10, alignItems: 'flex-end' }}>
              <div>
                <div style={{ fontSize: 9, color: '#6b7a8d', marginBottom: 3 }}>Mode</div>
                <select value={draft.mode || 'paper'} onChange={D('mode')} style={selStyle}>
                  <option value="paper">Paper (simulated fills)</option>
                  <option value="live">LIVE (real orders)</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize: 9, color: '#6b7a8d', marginBottom: 3 }}>Structure</div>
                <select value={draft.structure || 'debit'} onChange={D('structure')} style={selStyle}>
                  <option value="debit">Debit (pay premium)</option>
                  <option value="credit">Credit (receive premium)</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize: 9, color: '#6b7a8d', marginBottom: 3 }}>Carry overnight</div>
                <select value={String(!!draft.carry_overnight)} onChange={D('carry_overnight')} style={selStyle}>
                  <option value="false">No — exit at exit time</option>
                  <option value="true">Yes (NRML only)</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize: 9, color: '#6b7a8d', marginBottom: 3 }}>Strike reference</div>
                <select value={draft.strike_ref || 'spot'} onChange={D('strike_ref')} style={selStyle}>
                  <option value="spot">Spot</option>
                  <option value="future">Future</option>
                  <option value="synthetic">Synthetic future</option>
                </select>
              </div>
            </div>
            {String(draft.no_trade_after || '').trim() ? (
              <div style={{ fontSize: 10, color: '#6b7a8d', marginBottom: 10 }}>
                After {draft.no_trade_after} no new entry and no reversal. An open position is still
                managed and still exits normally{String(draft.carry_overnight) === 'true'
                  ? ' — but with carry on, a reversal is what closes a carried position, so it will keep carrying instead.'
                  : ' at ' + (draft.exit_time || '15:39') + '.'}
              </div>
            ) : null}
            {String(draft.product).toUpperCase() === 'NRML' && String(draft.carry_overnight) !== 'true' ? (
              <div style={{ fontSize: 10, color: '#6b7a8d', marginBottom: 10 }}>
                NRML without carry — the position still exits at {draft.exit_time || '15:39'}, it just is not MIS.
              </div>
            ) : null}
            {String(draft.carry_overnight) === 'true' && String(draft.product).toUpperCase() !== 'NRML' ? (
              <div style={{ fontSize: 10, color: '#fbbf24', marginBottom: 10 }}>
                Carry overnight needs Product = NRML. Under MIS the broker squares off regardless, so this will be ignored.
              </div>
            ) : null}
            {String(draft.carry_overnight) === 'true' && String(draft.product).toUpperCase() === 'NRML' ? (
              <div style={{ fontSize: 10, color: '#fbbf24', marginBottom: 10 }}>
                No time exit while carrying — a reverse signal or an MTM trigger is the only thing that closes the position.
              </div>
            ) : null}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
              <Field label="Wing offset (normal)" value={draft.wing_offset_normal} onChange={D('wing_offset_normal')} />
              <Field label="Wing offset (expiry)" value={draft.wing_offset_expiry} onChange={D('wing_offset_expiry')} />
            </div>

            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.8, color: '#556677', margin: '12px 0 6px' }}>TRADINGVIEW WEBHOOK</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 6 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 9, color: '#6b7a8d', marginBottom: 3 }}>Token (empty = every webhook rejected)</div>
                <input value={draft.webhook_token || ''} onChange={D('webhook_token')}
                  placeholder="generate or paste"
                  style={{ width: '100%', background: '#0d1524', border: '1px solid #26334a', borderRadius: 6,
                    padding: '7px 9px', color: '#e5e7eb', fontSize: 11, fontFamily: 'monospace', boxSizing: 'border-box' }} />
              </div>
              <button onClick={function () {
                fetch(API + '/directional-tv-1/generate-token', { method: 'POST' })
                  .then(function (r) { return r.json(); })
                  .then(function (d) { if (d && d.token) setDraft(function (p) { return Object.assign({}, p, { webhook_token: d.token }); }); })
                  .catch(function () {});
              }} style={{ fontSize: 11, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', borderRadius: 6,
                padding: '8px 12px', background: 'rgba(14,165,233,0.12)', color: '#0ea5e9', border: '1px solid rgba(14,165,233,0.4)' }}>
                Generate
              </button>
            </div>
            <div style={{ fontSize: 9, color: '#6b7a8d', marginBottom: 4 }}>Alert URL — paste into TradingView</div>
            <div style={{ background: '#0d1524', border: '1px solid #26334a', borderRadius: 6, padding: '7px 9px',
              fontSize: 10, fontFamily: 'monospace', color: '#8b98a9', marginBottom: 6, wordBreak: 'break-all' }}>
              {API + '/webhook/dtv1'}
            </div>
            <div style={{ fontSize: 9, color: '#6b7a8d', marginBottom: 4 }}>Alert message body</div>
            <div style={{ background: '#0d1524', border: '1px solid #26334a', borderRadius: 6, padding: '7px 9px',
              fontSize: 10, fontFamily: 'monospace', color: '#8b98a9', marginBottom: 10, whiteSpace: 'pre-wrap' }}>
              {'{"token": "' + (draft.webhook_token || '<token>') + '", "alert_type": "entry", "alert_message": "long"}'}
            </div>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.8, color: '#556677', margin: '12px 0 6px' }}>PROFIT MANAGEMENT (₹ per lot, 0 = off)</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
              <Field label="MTM SL" value={draft.mtm_sl_per_lot} onChange={D('mtm_sl_per_lot')} />
              <Field label="Target" value={draft.target_per_lot} onChange={D('target_per_lot')} />
              <Field label="Protect floor" value={draft.protect_per_lot} onChange={D('protect_per_lot')} />
              <Field label="Protect arms at" value={draft.protect_level_per_lot} onChange={D('protect_level_per_lot')} />
              <Field label="Trail step" value={draft.trail_step_per_lot} onChange={D('trail_step_per_lot')} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button onClick={function () { setCfgOpen(false); }}
                style={{ fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', borderRadius: 8, padding: '8px 16px', background: 'transparent', color: '#8b98a9', border: '1px solid #26334a' }}>Cancel</button>
              <button onClick={saveCfg}
                style={{ fontSize: 12, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', borderRadius: 8, padding: '8px 20px', background: 'rgba(56,189,248,0.14)', color: '#38bdf8', border: '1px solid rgba(56,189,248,0.5)' }}>Save</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DirectionalTV1;
